import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Eye, 
  Star, 
  Heart, 
  MessageSquare, 
  BookOpen,
  TrendingUp,
  ArrowLeft,
  Loader2,
  Calendar,
  User,
  ChevronRight,
  Clock,
  Award
} from 'lucide-react';

function StatCard({ title, value, icon: Icon, color = "ruby", subtitle, trend }) {
  return (
    <Card className="bg-surface border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-3xl font-bold text-foreground`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && (
              <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/20`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SimpleBarChart({ data, maxValue }) {
  if (!data || data.length === 0) return null;
  
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item, index) => {
        const height = maxValue > 0 ? (item.views / maxValue) * 100 : 0;
        return (
          <div 
            key={index} 
            className="flex-1 flex flex-col items-center gap-1"
            title={`${item.date}: ${item.views} visualizações`}
          >
            <div 
              className="w-full bg-ruby/80 rounded-t transition-all hover:bg-ruby"
              style={{ height: `${Math.max(height, 2)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

function RatingDistribution({ distribution }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  
  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map(star => {
        const count = distribution[star.toString()] || 0;
        const percentage = total > 0 ? (count / total) * 100 : 0;
        
        return (
          <div key={star} className="flex items-center gap-3">
            <div className="flex items-center gap-1 w-12">
              <span className="text-sm text-foreground">{star}</span>
              <Star className="w-3 h-3 text-gold fill-gold" />
            </div>
            <div className="flex-1">
              <Progress value={percentage} className="h-2 bg-white/10" />
            </div>
            <span className="text-sm text-muted-foreground w-8 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function AuthorAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_author) {
      toast.error('Apenas autores podem aceder às analytics');
      navigate('/');
      return;
    }
    loadAnalytics();
  }, [user, navigate]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/author/analytics');
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Erro ao carregar analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <p className="text-muted-foreground">Erro ao carregar dados</p>
        </div>
      </div>
    );
  }

  const maxViews = Math.max(...(analytics.views_by_day || []).map(d => d.views), 1);
  const last7Days = (analytics.views_by_day || []).slice(-7);
  const viewsLast7Days = last7Days.reduce((sum, d) => sum + d.views, 0);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/author')}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ruby to-ruby-light flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-foreground" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
                <p className="text-muted-foreground">Desempenho das tuas histórias</p>
              </div>
            </div>
          </div>
          <Badge className="bg-ruby/20 text-ruby border-ruby/30 px-4 py-2">
            <Award className="w-4 h-4 mr-2" />
            Autor
          </Badge>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Histórias Publicadas" 
            value={analytics.published_stories}
            subtitle={`${analytics.total_stories} total`}
            icon={BookOpen}
            color="blue-400"
          />
          <StatCard 
            title="Visualizações Totais" 
            value={analytics.total_views.toLocaleString()}
            subtitle={`${viewsLast7Days} nos últimos 7 dias`}
            icon={Eye}
            color="green-400"
          />
          <StatCard 
            title="Avaliação Média" 
            value={analytics.average_rating.toFixed(1)}
            subtitle={`${analytics.total_ratings} avaliações`}
            icon={Star}
            color="gold"
          />
          <StatCard 
            title="Favoritos" 
            value={analytics.total_favorites}
            subtitle={`${analytics.total_comments} comentários`}
            icon={Heart}
            color="ruby"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Charts */}
          <div className="lg:col-span-2 space-y-8">
            {/* Views Chart */}
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-ruby" />
                  Visualizações (Últimos 30 dias)
                </CardTitle>
                <CardDescription>Tendência de visualizações das tuas histórias</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.views_by_day && analytics.views_by_day.length > 0 ? (
                  <>
                    <SimpleBarChart data={analytics.views_by_day} maxValue={maxViews} />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>{formatDate(analytics.views_by_day[0]?.date)}</span>
                      <span>{formatDate(analytics.views_by_day[analytics.views_by_day.length - 1]?.date)}</span>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-8">Sem dados de visualização</p>
                )}
              </CardContent>
            </Card>

            {/* Top Stories */}
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Award className="w-5 h-5 text-ruby" />
                  Histórias Mais Populares
                </CardTitle>
                <CardDescription>As tuas histórias com melhor desempenho</CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.top_stories && analytics.top_stories.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_stories.map((story, index) => (
                      <div 
                        key={story.id}
                        className="flex items-center gap-4 p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors"
                        onClick={() => navigate(`/story/${story.slug || story.id}`)}
                      >
                        <div className="w-8 h-8 rounded-full bg-ruby/20 flex items-center justify-center text-ruby font-bold">
                          {index + 1}
                        </div>
                        <div className="w-12 h-16 rounded overflow-hidden shrink-0 bg-white/5">
                          {story.cover_image ? (
                            <img src={story.cover_image} alt={story.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground truncate">{story.title}</h4>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {story.views}
                            </span>
                            {story.rating > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-gold fill-gold" />
                                {story.rating.toFixed(1)} ({story.rating_count})
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">Publica histórias para ver os dados</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-8">
            {/* Rating Distribution */}
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Star className="w-5 h-5 text-gold" />
                  Distribuição de Avaliações
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RatingDistribution distribution={analytics.ratings_distribution} />
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total de avaliações</span>
                    <span className="text-foreground font-semibold">{analytics.total_ratings}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Distribution */}
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-ruby" />
                  Categorias
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.category_distribution && analytics.category_distribution.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.category_distribution.slice(0, 6).map((cat, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <Badge variant="outline" className="border-border">{cat.category}</Badge>
                        <span className="text-sm text-muted-foreground">{cat.count} {cat.count === 1 ? 'história' : 'histórias'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Sem categorias</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Comments */}
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-ruby" />
                  Comentários Recentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.recent_comments && analytics.recent_comments.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.recent_comments.slice(0, 5).map((comment, index) => (
                      <div key={index} className="p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-ruby/20 flex items-center justify-center">
                            <User className="w-3 h-3 text-ruby" />
                          </div>
                          <span className="text-sm font-medium text-foreground">{comment.user_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{comment.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          em <span className="text-ruby">{comment.story_title}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Sem comentários recentes</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
